import {
	defaultAttachmentTypeLabelMap,
	SuretyAccountId,
	SuretyIndemnityAgreementId,
} from "@inrev/common";
import {
	useDeleteIndemnityAgreementFile,
	useGetAccountIndemnityAgreementFile,
	useGetAccountIndemnityAgreementFileUrl,
} from "@portal/api/account/agent";
import { AttachmentFileCardLayout } from "@portal/components/layout/AttachmentLayout";
import { type PDFViewerControl } from "@portal/components/patterns/pdf-viewer";
import { AttachmentActions } from "@portal/components/ui/Attachment/AttachmentActions";
import { Tag } from "@portal/components/ui/Tag";
import { useMemo } from "react";

export const IndemnityAgreementAttachmentCard = ({
	suretyAccountId,
	indemnityAgreementId,
	isComplete,
}: {
	suretyAccountId: SuretyAccountId;
	indemnityAgreementId: SuretyIndemnityAgreementId;
	isComplete: boolean;
}) => {
	const { indemnityAgreementFile, indemnityAgreementFileLoading } =
		useGetAccountIndemnityAgreementFile(suretyAccountId, indemnityAgreementId);

	const { downloadIndemnityAgreement, indemnityAgreementFileUrlLoading } =
		useGetAccountIndemnityAgreementFileUrl({
			accountId: suretyAccountId,
			indemnityAgreementId,
			asDownload: true,
		});

	const { deleteIndemnityAgreementFile } = useDeleteIndemnityAgreementFile(
		suretyAccountId,
		indemnityAgreementId,
	);

	const indemnityFileId = indemnityAgreementFile?.id;
	const viewControl = useMemo<PDFViewerControl>(
		() => ({
			fetchUrl: `/v2/surety/accounts/${suretyAccountId}/indemnity-agreements/${indemnityAgreementId}/url`,
			queryKey: ["accounts", suretyAccountId, "indemnity-agreements", indemnityAgreementId, "url"],
			fileId: indemnityFileId,
		}),
		[suretyAccountId, indemnityAgreementId, indemnityFileId],
	);

	// if we just don't have a file for this agreement
	if (!indemnityAgreementFile) {
		return null;
	}

	const headerSection = indemnityAgreementFile.name;

	// Uncomment when after refactoring the UploadedFile type to include createdAt and uploadedBy
	// const subHeaderSection = (
	// 	<div className="text-tertiary text-[12px] flex gap-x-[40px]">
	// 		<div>
	// 			{indemnityAgreementFile.createdAt &&
	// 				DateTime.fromISO(indemnityAgreementFile.createdAt).toFormat("LLL dd, yyyy")}
	// 		</div>
	// 		{indemnityAgreementFile.uploadedBy && (
	// 			<div>
	// 				{indemnityAgreementFile.uploadedBy.firstName} {indemnityAgreementFile.uploadedBy.lastName}
	// 			</div>
	// 		)}
	// 	</div>
	// );

	const tagSection = (
		<>
			{indemnityAgreementFile.types.map((fileType) => (
				<Tag key={fileType}>{defaultAttachmentTypeLabelMap[fileType]}</Tag>
			))}
		</>
	);

	const actionsSection = (
		<AttachmentActions
			analyticsContext="indemnity"
			fileExtension={indemnityAgreementFile.extension}
			fileName={indemnityAgreementFile.name}
			enableDelete={!isComplete}
			enableDownload={true}
			enableView={true}
			viewControl={viewControl}
			onConfirmDelete={!isComplete ? deleteIndemnityAgreementFile : undefined}
			onConfirmDownload={downloadIndemnityAgreement}
			isLoading={indemnityAgreementFileUrlLoading}
		/>
	);

	return (
		<AttachmentFileCardLayout
			fileExtension={indemnityAgreementFile.extension}
			headerSection={headerSection}
			tagSection={tagSection}
			actionSection={actionsSection}
			isLoading={indemnityAgreementFileLoading}
		/>
	);
};
